/**
 * Page header used by every screen inside the shell.
 *
 * Centralised so that the `<h1>` of a page, its supporting sentence and its actions always share the same rhythm —
 * and so that heading order (one `h1` per page, WCAG 1.3.1) stays enforceable by review of a single file.
 */

import { cn } from '@/lib/utils';

/** Props accepted by {@link PageHeader}. */
export interface PageHeaderProps {
  /** Page title; rendered as the single `h1` of the screen. */
  title: string;
  /** Optional supporting sentence. */
  description?: string;
  /** Optional action area, aligned to the end edge. */
  actions?: React.ReactNode;
  /** Extra classes for the wrapper. */
  className?: string;
}

/** Renders the title block of a page. */
export function PageHeader({ title, description, actions, className }: PageHeaderProps) {
  return (
    <header
      className={cn('flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between', className)}
    >
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </div>

      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}
